/**
 * Supabase children テーブルの CRUD
 * ゲストユーザーは localStorage で管理
 */
import { supabase } from './supabaseClient';
import { deleteAllChildAudio } from './audioStorageApi';

const GUEST_PROFILES_KEY    = 'guest_profiles';
const GUEST_CURRENT_KEY     = 'guest_current_id';

// ──────────────────────────────────────────
// ゲスト用（localStorage）
// ──────────────────────────────────────────

export function loadGuestProfiles() {
  try {
    return JSON.parse(localStorage.getItem(GUEST_PROFILES_KEY) || '[]');
  } catch { return []; }
}

export function saveGuestProfile(profile) {
  const profiles = loadGuestProfiles();
  const newProfile = { ...profile, id: `guest_${Date.now()}` };
  profiles.push(newProfile);
  localStorage.setItem(GUEST_PROFILES_KEY, JSON.stringify(profiles));
  return newProfile;
}

export function updateGuestProfile(id, updates) {
  const profiles = loadGuestProfiles().map(p => p.id === id ? { ...p, ...updates } : p);
  localStorage.setItem(GUEST_PROFILES_KEY, JSON.stringify(profiles));
}

export function deleteGuestProfile(id) {
  const profiles = loadGuestProfiles().filter(p => p.id !== id);
  localStorage.setItem(GUEST_PROFILES_KEY, JSON.stringify(profiles));
  if (getGuestCurrentId() === id) setGuestCurrentId(profiles[0]?.id ?? null);
}

export function getGuestCurrentId() {
  return localStorage.getItem(GUEST_CURRENT_KEY);
}

export function setGuestCurrentId(id) {
  if (id) localStorage.setItem(GUEST_CURRENT_KEY, id);
  else localStorage.removeItem(GUEST_CURRENT_KEY);
}

// ──────────────────────────────────────────
// ログインユーザー用（Supabase）
// ──────────────────────────────────────────

/** 自分の family_id を取得（auth.uid = families.id） */
async function getFamilyId() {
  // getSession はローカルキャッシュから取得するため 403 が起きない
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id ?? null;
}

/** children 一覧を取得 */
export async function fetchChildren() {
  const familyId = await getFamilyId();
  if (!familyId) return [];
  const { data, error } = await supabase
    .from('children')
    .select('*')
    .eq('family_id', familyId)
    .order('created_at', { ascending: true });
  if (error) { console.error('fetchChildren:', error); return []; }
  return data;
}

/** child を作成 */
export async function createChild({ name, avatar, birth_year, birth_month, pin, setup_role }) {
  const familyId = await getFamilyId();
  console.log('[createChild] familyId:', familyId);
  if (!familyId) {
    console.error('[createChild] familyId is null - user not logged in?');
    return null;
  }
  const { data, error } = await supabase
    .from('children')
    .insert({
      family_id: familyId, name, avatar, birth_year, birth_month,
      pin: pin || null,
      setup_role: setup_role || null,
    })
    .select()
    .single();
  if (error) {
    console.error('[createChild] error:', error.message, error.details, error.hint);
    return null;
  }
  console.log('[createChild] success:', data);
  return data;
}

/** child を更新 */
export async function updateChild(id, updates) {
  const { error } = await supabase
    .from('children')
    .update(updates)
    .eq('id', id);
  if (error) console.error('updateChild:', error);
}

/** child を削除（Storageの音声ファイルも一緒に削除） */
export async function deleteChild(id) {
  // Storageの音声を先に削除
  await deleteAllChildAudio(id);
  // DBのchildrenレコードを削除（reading_logsはCASCADEで自動削除）
  const { error } = await supabase
    .from('children')
    .delete()
    .eq('id', id);
  if (error) console.error('deleteChild:', error);
}

/** 最後に選んだ child_id を localStorage に保存 */
const CURRENT_CHILD_KEY = 'current_child_id';
export function saveCurrentChildId(id) {
  if (id) localStorage.setItem(CURRENT_CHILD_KEY, id);
  else localStorage.removeItem(CURRENT_CHILD_KEY);
}
export function loadCurrentChildId() {
  return localStorage.getItem(CURRENT_CHILD_KEY);
}
